import { useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import Dialog from "../dialog/Dialog";

const extractCreatedActivity = (response) => {
  const body = response?.data ?? response;
  if (body?.id) {
    return body;
  }
  if (body?.data?.id) {
    return body.data;
  }
  return null;
};

const InlineActivityMasterCreateDialog = ({
  open,
  onClose,
  onCreated,
  createFn,
  queryKey = ["activities"],
  title = "Add Activity",
}) => {
  const [name, setName] = useState("");
  const [error, setError] = useState("");
  const queryClient = useQueryClient();

  const reset = () => {
    setName("");
    setError("");
  };

  const handleClose = () => {
    reset();
    onClose();
  };

  const mutation = useMutation({
    mutationFn: createFn,
    onSuccess: async (response) => {
      const created = extractCreatedActivity(response);
      const newId = created?.id;

      if (newId && created?.name) {
        queryClient.setQueryData(queryKey, (old) => {
          const list = old?.data ?? [];
          const exists = list.some(
            (item) => Number(item.id) === Number(newId)
          );
          if (exists) {
            return old;
          }
          return {
            ...old,
            data: [...list, { id: newId, name: created.name }],
          };
        });
      }

      await queryClient.invalidateQueries({ queryKey });

      if (newId) {
        onCreated?.(Number(newId));
      }
      handleClose();
    },
    onError: (err) => {
      setError(err?.response?.data?.message || "Failed to create activity.");
    },
  });

  const handleSubmit = () => {
    const trimmed = name.trim();
    if (!trimmed) {
      setError("Activity name is required.");
      return;
    }
    setError("");
    mutation.mutate({ name: trimmed });
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      title={title}
      width="w-[520px]"
      closeButtonText="Cancel"
      primaryButtonText="Save"
      primaryButtonAction={handleSubmit}
      isLoading={mutation.isPending}
      isPrimaryDisabled={!name.trim()}
    >
      <div className="space-y-4 p-1">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Activity name <span className="text-red-500">*</span>
          </label>
          <input
            autoFocus
            type="text"
            value={name}
            onChange={(e) => {
              setName(e.target.value);
              setError("");
            }}
            onKeyDown={(e) => {
              if (e.key === "Enter") handleSubmit();
            }}
            className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:border-primary"
            placeholder="Enter activity name"
          />
        </div>

        {error && <p className="text-red-500 text-xs">{error}</p>}
      </div>
    </Dialog>
  );
};

export default InlineActivityMasterCreateDialog;
